<script setup>
// O rastro de um turno do agente: o status, as ferramentas que rodaram, com o desfecho de cada uma
// (e da busca na base de conhecimento), a passagem a uma pessoa e a manutenção. Saiu do Playground
// quando o bloco do link de teste passou a mostrar o rastro dos turnos (AGENTS-LINK-01, L-21): um
// cartão só, sobre a mesma projeção do rastro (`PowerAgents::TraceProjection`), sem formato novo.
//
// `turno` é a projeção de um turno com `handoff`, `maintenance` e `attachments` ao lado — este, o
// que o agente fez com cada foto e PDF da mensagem, no Playground e no link (L-23); o que for só de
// quem usa o cartão (o funil do link) entra pelo slot, no fim.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { leituraDaBusca, previaIndentada } from 'dashboard/helper/agentTrace';
import { useMotivoDoArquivo } from 'shared/components/conversaDeTeste/motivoDoArquivo';

defineProps({
  turno: { type: Object, required: true },
  // O nome do time para onde a conversa iria; sem ele, o número.
  nomeDoTime: { type: Function, default: id => `#${id}` },
});

const { t } = useI18n();
const motivoDoArquivo = useMotivoDoArquivo();
const expandedArgs = ref({});

// A tool de conhecimento devolve, além dos trechos, EM QUANTAS bases buscou, qual foi o
// melhor escore antes do corte e DE ONDE veio cada trecho. A leitura mora em
// `helper/agentTrace` porque o painel de depuração da conversa lê o mesmo formato.
const conhecimento = leituraDaBusca;

// O desfecho da busca em UMA linha, para o cabeçalho do cartão fechado.
function resumoDaBusca(call) {
  const r = conhecimento(call);
  if (!r) return '';
  if (r.estado === 'sem_base') return t('POWER_AGENTS.PLAYGROUND.KB_NO_BASE');
  if (r.estado === 'vazio')
    return t('POWER_AGENTS.PLAYGROUND.KB_EMPTY', { n: r.bases.length });
  return t('POWER_AGENTS.PLAYGROUND.KB_SHORT', {
    n: r.achou,
    v: Number(r.topo).toFixed(2),
  });
}

function argsPreview(args) {
  if (args == null) return '';
  return typeof args === 'string' ? args : JSON.stringify(args, null, 2);
}

function toggleArgs(key) {
  expandedArgs.value = {
    ...expandedArgs.value,
    [key]: !expandedArgs.value[key],
  };
}
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
  >
    <div class="flex flex-wrap items-center gap-2">
      <span
        class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-n-teal-3 text-n-teal-11"
      >
        <i class="i-lucide-check" />
        {{ turno.status }}
      </span>
      <!-- Só para super admin: para os outros o servidor não manda o modelo. -->
      <code v-if="turno.model" class="font-mono text-[11px] text-n-slate-11">
        {{ turno.model }}
      </code>
      <span v-if="turno.cache_read" class="text-[11px] text-n-teal-11">
        {{ t('POWER_AGENTS.PLAYGROUND.TURN_CACHE', { n: turno.cache_read }) }}
      </span>
      <span
        v-if="turno.latency_ms != null"
        class="ml-auto text-[11px] text-n-slate-9"
      >
        {{
          t('POWER_AGENTS.PLAYGROUND.TURN_LATENCY', { ms: turno.latency_ms })
        }}
      </span>
    </div>

    <!-- O pedido de humano no teste é simulado: diz para onde IRIA, sem abrir nada. -->
    <p
      v-if="turno.handoff?.outcome === 'ok'"
      class="px-2 py-1 text-xs rounded bg-n-amber-3 text-n-amber-11"
    >
      {{
        turno.handoff.team_id
          ? t('POWER_AGENTS.PLAYGROUND.HANDOFF_WOULD', {
              team: nomeDoTime(turno.handoff.team_id),
            })
          : t('POWER_AGENTS.PLAYGROUND.HANDOFF_WOULD_QUEUE')
      }}
    </p>
    <p
      v-else-if="turno.handoff?.outcome === 'policy_denied'"
      class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
    >
      {{ t('POWER_AGENTS.PLAYGROUND.HANDOFF_DENIED') }}
    </p>
    <p
      v-if="turno.maintenance"
      class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
    >
      {{ t('POWER_AGENTS.PLAYGROUND.MAINTENANCE_REPLY') }}
    </p>

    <p
      v-if="!turno.tool_calls || !turno.tool_calls.length"
      class="text-[11px] text-n-slate-9"
    >
      {{ t('POWER_AGENTS.PLAYGROUND.TRACE_NO_TOOLS') }}
    </p>
    <div
      v-for="(call, i) in turno.tool_calls || []"
      :key="`${turno.id}-${i}`"
      class="flex flex-col gap-1 px-2.5 py-2 border rounded-lg border-n-weak bg-n-background"
    >
      <div
        class="flex flex-wrap items-center gap-2 cursor-pointer"
        role="button"
        tabindex="0"
        @click="toggleArgs(`${turno.id}-${i}`)"
        @keydown.enter="toggleArgs(`${turno.id}-${i}`)"
      >
        <i class="text-xs i-lucide-wrench text-n-slate-10" />
        <code class="font-mono text-xs text-n-slate-12">
          {{ call.tool_name }}
        </code>
        <!-- Simulada: o pedido de humano no teste não abre nem atribui nada. Dito na
             marca, para ninguém sair do teste achando que abriu. -->
        <span
          v-if="call.execution === 'simulated'"
          class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-amber-3 text-n-amber-11"
        >
          {{ t('POWER_AGENTS.PLAYGROUND.SIMULATED') }}
        </span>
        <span
          v-else
          class="px-1.5 py-0.5 text-[10px] font-medium rounded"
          :class="
            call.status === 'ok'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          {{ call.status }}
        </span>
        <!-- Alterou dados: rodou de verdade e mudou algo no sistema do cliente (fechar
             chamado, cadastrar, enviar). O teste não simula escrita, então quem testa
             precisa ver o que aconteceu de fato. Escrita que falhou não ganha a marca: o
             status de erro ao lado já diz o que houve. -->
        <span
          v-if="
            call.execution === 'ran' &&
            call.efeito === 'escrita' &&
            call.status === 'ok'
          "
          class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-blue-3 text-n-blue-11"
        >
          {{ t('POWER_AGENTS.PLAYGROUND.WROTE') }}
        </span>
        <span v-if="call.http_status" class="text-[11px] text-n-slate-10">
          {{
            t('POWER_AGENTS.PLAYGROUND.TOOL_HTTP', {
              status: call.http_status,
            })
          }}
        </span>
        <span v-if="call.latency_ms != null" class="text-[11px] text-n-slate-9">
          {{
            t('POWER_AGENTS.PLAYGROUND.TURN_LATENCY', {
              ms: call.latency_ms,
            })
          }}
        </span>
        <!-- O desfecho da busca cabe no cabeçalho, e é o que evita ter de abrir para
             saber se deu certo: "5 trechos · 0.95" responde a pergunta comum. -->
        <span
          v-if="conhecimento(call)"
          class="px-1.5 py-0.5 text-[10px] rounded"
          :class="{
            'bg-n-amber-3 text-n-amber-11':
              conhecimento(call).estado === 'sem_base',
            'bg-n-alpha-2 text-n-slate-11':
              conhecimento(call).estado === 'vazio',
            'bg-n-teal-3 text-n-teal-11': conhecimento(call).estado === 'achou',
          }"
        >
          {{ resumoDaBusca(call) }}
        </span>
        <i
          class="ml-auto text-xs text-n-slate-10"
          :class="
            expandedArgs[`${turno.id}-${i}`]
              ? 'i-lucide-chevron-up'
              : 'i-lucide-chevron-down'
          "
        />
      </div>
      <div v-if="expandedArgs[`${turno.id}-${i}`]" class="flex flex-col gap-1">
        <pre
          class="max-h-40 overflow-auto p-2 text-[11px] font-mono rounded bg-n-alpha-1 text-n-slate-11"
          >{{ argsPreview(call.args) }}</pre>
        <!-- A resposta da tool é o que se abre o rastro para ler: indentada como o `args`, e
             numa caixa que cabe inteira na conversa do Playground (26rem), para a rolagem dela
             não brigar com a da página. Rolar, sim; cortar, não. -->
        <pre
          v-if="call.response_preview"
          class="max-h-80 overflow-auto p-2 text-[11px] font-mono rounded bg-n-alpha-1 text-n-slate-11"
          data-resposta-da-tool
          >{{ previaIndentada(call.response_preview) }}</pre>
      </div>
      <!-- Busca em base de conhecimento: o status HTTP não distingue "não tinha onde
           buscar" de "buscou em duas bases e não achou". Estes dois campos distinguem. -->
      <div
        v-if="conhecimento(call) && expandedArgs[`${turno.id}-${i}`]"
        class="flex flex-wrap items-center gap-2 text-[11px]"
      >
        <span
          class="px-1.5 py-0.5 rounded"
          :class="{
            'bg-n-amber-3 text-n-amber-11':
              conhecimento(call).estado === 'sem_base',
            'bg-n-alpha-2 text-n-slate-11':
              conhecimento(call).estado === 'vazio',
            'bg-n-teal-3 text-n-teal-11': conhecimento(call).estado === 'achou',
          }"
        >
          {{
            conhecimento(call).estado === 'sem_base'
              ? t('POWER_AGENTS.PLAYGROUND.KB_NO_BASE')
              : conhecimento(call).estado === 'vazio'
                ? t('POWER_AGENTS.PLAYGROUND.KB_EMPTY', {
                    n: conhecimento(call).bases.length,
                  })
                : t('POWER_AGENTS.PLAYGROUND.KB_HIT', {
                    n: conhecimento(call).achou,
                    bases: conhecimento(call).bases.length,
                  })
          }}
        </span>
        <span v-if="conhecimento(call).topo != null" class="text-n-slate-10">
          {{
            t('POWER_AGENTS.PLAYGROUND.KB_TOP', {
              v: Number(conhecimento(call).topo).toFixed(2),
            })
          }}
        </span>
        <span
          v-for="base in conhecimento(call).bases"
          :key="base.id"
          class="px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-10"
        >
          {{ base.name }}
        </span>
      </div>

      <!-- As fontes que responderam, com o escore. "Trouxe 3 trechos" e "a resposta saiu
           desta página" são perguntas diferentes, e a segunda é a que se faz quando a
           resposta sai errada. -->
      <ol
        v-if="
          expandedArgs[`${turno.id}-${i}`] &&
          conhecimento(call) &&
          conhecimento(call).fontes.length
        "
        class="flex flex-col gap-0.5 pl-1"
      >
        <li
          v-for="(fonte, j) in conhecimento(call).fontes"
          :key="j"
          class="flex items-baseline gap-2 text-[11px]"
        >
          <span class="font-mono text-n-slate-9">{{ fonte.score }}</span>
          <a
            v-if="fonte.source_url"
            :href="fonte.source_url"
            target="_blank"
            rel="noopener noreferrer"
            class="truncate text-n-brand hover:underline"
          >
            {{ fonte.filename || fonte.source_url }}
          </a>
          <span v-else class="truncate text-n-slate-11">
            {{ fonte.filename || fonte.chunk_id }}
          </span>
        </li>
        <li
          v-if="conhecimento(call).fontesOcultas"
          class="text-[11px] text-n-slate-9"
        >
          {{
            t('POWER_AGENTS.PLAYGROUND.KB_HIDDEN', {
              count: conhecimento(call).fontesOcultas,
            })
          }}
        </li>
      </ol>

      <p v-if="call.error" class="text-[11px] text-n-ruby-10">
        {{ call.error }}
      </p>
    </div>
    <p
      v-for="(arquivo, i) in turno.attachments || []"
      :key="`${turno.id}-arquivo-${i}`"
      class="px-2 py-1 text-xs break-words rounded"
      :class="
        arquivo.read
          ? 'bg-n-alpha-2 text-n-slate-11'
          : 'bg-n-amber-3 text-n-amber-11'
      "
      data-arquivo-do-rastro
    >
      {{
        arquivo.read
          ? t('POWER_AGENTS.PLAYGROUND.TRACE_FILE_READ', {
              nome: arquivo.file_name,
            })
          : t('POWER_AGENTS.PLAYGROUND.TRACE_FILE_NOT_READ', {
              nome: arquivo.file_name,
              motivo: motivoDoArquivo(arquivo.reason),
            })
      }}
    </p>
    <slot />
  </div>
</template>
